import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, MessageCircle, Headphones } from "lucide-react";
import { APP_VERSION } from "@/version";

const WHATSAPP_URL = "https://wa.me/971558945236";

export const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <>
      {/* Support/Contact Banner */}
      <section className="py-10 sm:py-12 lg:py-16 bg-gradient-to-r from-gold-500/10 via-gold-500/5 to-gold-500/10 border-t border-gold-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8">
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gold-500/20 rounded-full flex items-center justify-center shrink-0">
                <Headphones className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-gold-500" />
              </div>
              <div>
                <h3 className="font-display text-xl sm:text-2xl mb-1">{t('footer.needHelp')}</h3>
                <p className="text-sm sm:text-base text-slate-400">{t('footer.tagline')}</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:gap-6 w-full sm:w-auto">
              <a 
                href="tel:+971558945236" 
                className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-gold-500/20 hover:bg-gold-500/30 border border-gold-500/30 rounded-sm transition-all group w-full sm:w-auto"
                data-testid="support-phone-btn"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500 group-hover:scale-110 transition-transform" />
                <span className="text-base sm:text-lg font-medium text-gold-500">+971 55 894 5236</span>
              </a>
              <a 
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-green-600 hover:bg-green-500 text-white rounded-sm transition-all group w-full sm:w-auto"
                data-testid="whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                <span className="text-sm sm:text-base">{t('footer.whatsapp')}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 border-t border-slate-800/50 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="col-span-2 sm:col-span-2 md:col-span-1">
              <h3 className="font-display text-xl sm:text-2xl text-gold-gradient mb-3 sm:mb-4">ExpertBase</h3>
              <p className="text-slate-500 text-xs sm:text-sm mb-3 sm:mb-4">{t('footer.tagline')}</p>
              <p className="text-slate-600 text-xs">
                Dubai, United Arab Emirates
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('footer.platform')}</h4>
              <ul className="space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/experts" className="hover:text-gold-500 transition-colors">{t('footer.browseExperts')}</Link></li>
                <li><Link to="/become-expert" className="hover:text-gold-500 transition-colors">{t('footer.becomeExpert')}</Link></li>
                <li><Link to="/enterprise" className="hover:text-gold-500 transition-colors">{t('footer.enterprise')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('footer.company')}</h4>
              <ul className="space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/about" className="hover:text-gold-500 transition-colors">{t('footer.aboutUs')}</Link></li>
                <li><a href="mailto:careers@expertbase.ai" className="hover:text-gold-500 transition-colors">Careers</a></li>
                <li><a href="mailto:blog@expertbase.ai" className="hover:text-gold-500 transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/privacy" className="hover:text-gold-500 transition-colors">{t('footer.privacyPolicy')}</Link></li>
                <li><Link to="/terms" className="hover:text-gold-500 transition-colors">{t('footer.termsOfService')}</Link></li>
                <li><Link to="/contact" className="hover:text-gold-500 transition-colors">{t('footer.contact')}</Link></li>
              </ul>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('footer.support')}</h4>
              <ul className="space-y-2 text-slate-400 text-xs sm:text-sm">
                <li>
                  <a href="tel:+971558945236" className="hover:text-gold-500 transition-colors flex items-center gap-2">
                    <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                    +971 55 894 5236
                  </a>
                </li>
                <li>
                  <a 
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-500 transition-colors flex items-center gap-2"
                  >
                    <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    WhatsApp
                  </a>
                </li>
                <li><Link to="/contact" className="hover:text-gold-500 transition-colors">{t('footer.helpCenter')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 sm:pt-8 border-t border-slate-800/50 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 text-slate-500 text-xs sm:text-sm">
            <span className="text-center sm:text-left">{t('footer.copyright')}</span>
            <span className="text-slate-600">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>
    </>
  );
};
